// Live prices for the tokens a wallet holds — read from the shared store, and
// fed into it from the tape.
//
// The store is what makes the portfolio and the token page agree: whoever
// learns a price publishes it there, so a token has ONE last-known price in the
// app (and across tabs) instead of one per screen. See `lib/livePriceStore.ts`
// for the 1.2% gap that made this necessary.
//
// The tape half is here because the app-wide `trades` channel already carries
// every trade on the platform — the pair page subscribes to it too
// (`pairWsBootstrap.ts:79`) — so this is one subscription and no new load.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { TradeSummary } from '../api/types'
import { candleTradePrice } from '../lib/candles'
import type { LivePriceMap } from '../lib/livePortfolioValue'
import { getLivePrice, publishLivePrice, subscribeLivePrices } from '../lib/livePriceStore'
import { isDustQuote } from '../lib/markGuard'
import { TETHER_USDT_MASTER } from '../lib/searchDefault'
import { isTonQuoteToken } from '../lib/tonQuote'
import { useWsSubscription } from './useWsSubscription'

type TradeFrame = TradeSummary & { base_token?: string; pool_addr?: string }

function positiveOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null
}

/**
 * The price a tick tells, in GRAM (and USD when known), or null when it tells
 * none:
 *  - a dust print says nothing (the server's mark rule, lib/markGuard): an
 *    EQDAMs166… tick would have read 187 864 TON a token;
 *  - spot, not the execution price — the rule the candle already states: a
 *    position is worth the pool's price, not someone else's slippage;
 *  - a USD₮ pool prices the token in USD; the store holds GRAM per token, so
 *    the tick is converted at GRAM/USD now — without it, none (review N2: GRO,
 *    XAUt0 are marked in USD₮ pools). Only when that pool is a held row's
 *    mark pool (markPools): the store is read by token alone by Rail, Instant
 *    Trade, the watchlist and price alerts, so any other USD₮ pool's tick
 *    stays out of it, as before (a NOT/USD₮ fill reads ~1.5× NOT/TON);
 *  - a jetton quote prices the token in that jetton, and a frame that names no
 *    quote is not guessed to be TON: this price sizes a Sell Init offer.
 */
function tickPrice(
  trade: TradeFrame,
  gramUsdRate: number | null,
  markPool: string | undefined
): { priceTon: number; priceUsd: number | null } | null {
  if (isDustQuote(trade.quote_amount, trade.quote_token)) return null
  const candle = positiveOrNull(candleTradePrice(trade))
  if (candle === null) return null
  if (isTonQuoteToken(trade.quote_token)) {
    return { priceTon: candle, priceUsd: positiveOrNull(trade.price_usd) }
  }
  if (trade.quote_token === TETHER_USDT_MASTER) {
    if (!markPool || trade.pool_addr !== markPool) return null
    const rate = positiveOrNull(gramUsdRate)
    return rate === null ? null : { priceTon: candle / rate, priceUsd: candle }
  }
  return null
}

/**
 * @param tokens The masters to watch. TON-pool ticks for anything else are
 *   still published to the store — the token the operator has just bought is
 *   not in this list yet, and that tick is the one that fills its cell — but
 *   they do not re-render this consumer. A USD₮-pool tick is published only
 *   from a held row's mark pool (markPools), so a token marked in a USD₮ pool
 *   has no live price until the read that names its mark pool.
 */
export function useLiveTokenPrices(
  tokens: readonly string[],
  /** GRAM/USD now: a USD₮-pool tick is published in GRAM at it (review N2). */
  gramUsdRate: number | null = null,
  /** Each held token's mark pool: the only USD₮ pool whose ticks it publishes. */
  markPools: Readonly<Record<string, string>> = {}
): LivePriceMap {
  const watched = useMemo(() => new Set(tokens), [tokens])
  const watchedRef = useRef(watched)
  watchedRef.current = watched
  const rateRef = useRef(gramUsdRate)
  rateRef.current = gramUsdRate
  const markPoolsRef = useRef(markPools)
  markPoolsRef.current = markPools
  const [version, setVersion] = useState(0)

  // Filtering happens HERE and not at the tick, because those are two different
  // questions: what is worth STORING (everything — the store is the app's
  // shared answer, and a token joins this list a moment after its price
  // arrives) versus what is worth RE-RENDERING for. This provider sits above
  // the router, so a notification for a token nobody holds would re-render the
  // pair Rail and Instant Trade along with the portfolio.
  useEffect(
    () =>
      subscribeLivePrices((token) => {
        if (!watchedRef.current.has(token)) return
        setVersion((n) => n + 1)
      }),
    []
  )

  useWsSubscription<TradeFrame>('trades', (msg) => {
    if (msg.event !== 'trade') return
    const trade = msg.data
    const token = trade?.base_token
    if (!token) return
    const price = tickPrice(trade, rateRef.current, markPoolsRef.current[token])
    if (!price) return
    // The trade's own chain time is what the price DESCRIBES; the store stamps
    // when we HEARD it by itself. Keeping the two apart is what stops a canon
    // read, whose clock is the browser's, from looking newer than every tick.
    publishLivePrice(token, {
      ...price,
      chainAt: trade.ts,
      ...(trade.pool_addr ? { pool: trade.pool_addr } : {}),
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: version is the store's change edge, not a value read here
  return useMemo(() => {
    // Asks for the eight tokens this wallet holds, not for a copy of every
    // token ever traded on the platform — the store holds all of them by
    // design, and snapshotting the whole map on every tick would make the fix
    // for the em-dash pay for itself in allocations.
    const mine: Record<string, NonNullable<ReturnType<typeof getLivePrice>>> = {}
    for (const token of watched) {
      const price = getLivePrice(token)
      if (price) mine[token] = price
    }
    return mine
  }, [watched, version])
}
